import { BaseComponent } from "./base_component"
import { Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { ProjectProperties } from "."
import { booleanDataSocket } from "../socket_types"
import { CheckboxSelectControl } from "../controls/checkbox-select"
import { Polygon } from "ol/geom"
import { GeoJSON } from "ol/format"
import { maskFromExtentAndShape, zoomFromExtent } from "../bounding_box"
import { BooleanTileGrid } from "../tile_grid"
import { createXYZ } from "ol/tilegrid"
import { fromExtent } from "ol/geom/Polygon"
import { MAX_TILES } from "../../editor/project_editor"

export interface AvailableExtent {
    name: string
    id: number
    level?: number | null
}

export async function fetchExtentInfo(extentId: number) {
    const response = await fetch(`/extents/${extentId}/info`)
    const data = await response.json()

    return data
}

export class ExtentsComponent extends BaseComponent {
    projectProps: ProjectProperties
    extentList: AvailableExtent[]

    constructor(projectProps : ProjectProperties, extentList: AvailableExtent[]) {
        super("Extents")
        this.category = "Inputs"
        this.projectProps = projectProps
        this.extentList = extentList
    }

    getSelectedExtentIds(data: { [key: string]: unknown }): number[] {
        const extentIds = data.extentIds
        // Handle backwards compatibility: if extentId exists (old format), convert it
        if (extentIds === undefined || extentIds === null) {
            const oldExtentId = data.extentId
            if (oldExtentId !== undefined && oldExtentId !== null) {
                // Migrate old single value to new array format
                return [Number(oldExtentId)]
            }
            return []
        }
        // New format: array of string IDs
        if (Array.isArray(extentIds)) {
            return extentIds.map(id => Number(id))
        }
        return []
    }

    updateOutputs(node: Node) {
        // Remove all existing connections and outputs
        node.getConnections().forEach(c => {
            if (c.output.node !== node) {
                this.editor?.removeConnection(c)
            }
        })
        node.getConnections().forEach(c => this.editor?.removeConnection(c))
        Array.from(node.outputs.values()).forEach(output => node.removeOutput(output))

        const selectedIds = this.getSelectedExtentIds(node.data)
        
        if (selectedIds.length === 0) {
            // No selection, add default output
            node.addOutput(new Output("out", "Extent", booleanDataSocket))
        } else if (selectedIds.length === 1) {
            // Single selection, use generic "Extent" name
            node.addOutput(new Output("out", "Extent", booleanDataSocket))
        } else {
            // Multiple selections, add output for each with extent name
            selectedIds.forEach(extentId => {
                const extent = this.extentList.find(e => e.id === extentId)
                const outputName = extent ? extent.name : `extent_${extentId}`
                // Sanitize output key (remove spaces, special chars)
                const outputKey = outputName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()
                node.addOutput(new Output(outputKey, outputName, booleanDataSocket))
            })
            // Add ALL output for multiple selections
            node.addOutput(new Output("all", "ALL", booleanDataSocket))
        }

        node.update()
    }

    async builder(node: Node) {
        // Handle backwards compatibility: migrate old extentId to new extentIds format
        if (node.data.extentId !== undefined && node.data.extentId !== null && node.data.extentIds === undefined) {
            node.data.extentIds = [String(node.data.extentId)]
        }

        // Initialize with first extent if none selected
        if (this.extentList.length > 0 && (!node.data.extentIds || (Array.isArray(node.data.extentIds) && node.data.extentIds.length === 0))) {
            node.data.extentIds = [String(this.extentList[0].id)]
        }

        // Helper function to calculate indentation prefix based on level
        const getIndentationPrefix = (level: number | null | undefined): string => {
            if (level === null || level === undefined || level === 0) return ''
            // Simple indentation using non-breaking spaces
            const nbsp = '\u00A0' // non-breaking space
            // 2 non-breaking spaces per level for clean indentation
            return (nbsp + nbsp).repeat(level)
        }

        node.addControl(new CheckboxSelectControl(
            this.editor,
            "extentIds",
            () => this.extentList.map(e => ({ 
                id: e.id, 
                name: getIndentationPrefix(e.level) + e.name 
            })),
            () => {
                this.updateOutputs(node)
            },
            "Extent",
            "400px" // Minimum width for extents to accommodate indentation
        ))

        this.updateOutputs(node)
    }

    async processExtent(extentId: number, mask: BooleanTileGrid): Promise<BooleanTileGrid> {
        const extentInfo = await fetchExtentInfo(extentId)
         
        const tileGrid = createXYZ()
        const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)

        const zoom = this.projectProps.zoom

        let out = new BooleanTileGrid(
            this.projectProps.zoom,
            outputTileRange.minX,
            outputTileRange.minY,
            outputTileRange.getWidth(),
            outputTileRange.getHeight()
        )

        if (extentInfo.shape){
            const shape = JSON.parse(extentInfo.shape)
            const geojson = new GeoJSON().readFeature(shape)
            const polygon = geojson.getGeometry() as Polygon

            out.iterate(function(x, y, value){
                const center = tileGrid.getTileCoordCenter([zoom, x, y])
                if(polygon.intersectsCoordinate(center)){
                    out.set(x, y, mask.get(x, y))
                }
            })

        }else if (extentInfo.cql && extentInfo.layer){

            const pProps : ProjectProperties = {
                extent: extentInfo.value,
                zoom: zoomFromExtent(extentInfo.value, MAX_TILES),
                maskLayer: extentInfo.layer,
                maskCQL: extentInfo.cql,
                mask: true,
                maskShape: undefined,
                externalLink: undefined,
                debug: false,
                setExtraInfo: this.projectProps.setExtraInfo,
            }
            // some of these take a while to populate so using the mask function to take advantage of the caching logic. 
            const shape = await maskFromExtentAndShape(pProps)

            out.iterate(function(x, y, value){
                if(shape.get(x, y, zoom)){
                    out.set(x, y, mask.get(x, y))
                }
            })
     
        }else{
            const shape = fromExtent(extentInfo.value)

            out.iterate(function(x, y, value){
                const center = tileGrid.getTileCoordCenter([zoom, x, y])
                if(shape.intersectsCoordinate(center)){
                    out.set(x, y, mask.get(x, y))
                }
            })
        }

        return out
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const selectedIds = this.getSelectedExtentIds(node.data)
        
        if (selectedIds.length === 0) {
            // No extents selected, return empty output
            const tileGrid = createXYZ()
            const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)
            outputs.out = new BooleanTileGrid(
                this.projectProps.zoom,
                outputTileRange.minX,
                outputTileRange.minY,
                outputTileRange.getWidth(),
                outputTileRange.getHeight()
            )
            return
        }

        const mask = await maskFromExtentAndShape(this.projectProps)

        if (selectedIds.length === 1) {
            // Single extent: use "out" output key
            const extentId = selectedIds[0]
            outputs.out = await this.processExtent(extentId, mask)
        } else {
            // Multiple extents: output each with its name as key
            const extentOutputs: BooleanTileGrid[] = []
            for (const extentId of selectedIds) {
                const extent = this.extentList.find(e => e.id === extentId)
                const outputName = extent ? extent.name : `extent_${extentId}`
                const outputKey = outputName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()
                const extentOutput = await this.processExtent(extentId, mask)
                outputs[outputKey] = extentOutput
                extentOutputs.push(extentOutput)
            }
            
            // Create ALL output: OR operation across all extents
            const tileGrid = createXYZ()
            const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)
            const all = new BooleanTileGrid(
                this.projectProps.zoom,
                outputTileRange.minX,
                outputTileRange.minY,
                outputTileRange.getWidth(),
                outputTileRange.getHeight()
            )
            
            // ALL: set to true if any extent has true at that position
            all.iterate((x, y, value) => {
                const isInAnyExtent = extentOutputs.some(extentOutput => extentOutput.get(x, y))
                if (isInAnyExtent) {
                    all.set(x, y, true)
                }
            })
            
            outputs.all = all
        }
    }


}